import type { AnalyticsQuery } from "schemas/generated/analyticsRead";

import * as React from "react";

import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import { generateFakeChartDataRangeResult } from "@v2/features/analytics/utils/fakeData";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { analyticsRouter } from "@v2/trpc/routes/analytics";

/**
 * Standalone analytics read hook for use outside the Analytics dashboard.
 * Takes a fully-constructed AnalyticsQuery and fetches results from ClickHouse.
 *
 * Unlike useBasicAnalyticsRead, this does not depend on AnalyticsQueryContext
 * and can be used anywhere in the component tree.
 */
const useDirectAnalyticsRead = ({ query }: { query: AnalyticsQuery }) => {
  const currency = useCurrentAnalyticsCurrencyCode();
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const { shouldBlurFullScreen } = useAnalyticsStatus();

  const isUrlHostsLoading =
    query.urlHosts !== undefined && query.urlHosts.length === 0;

  const shouldFetch = Boolean(workspaceId || projectId) && !isUrlHostsLoading;

  const hasData = analyticsRouter.hasData.useQuery(
    { workspaceId: workspaceId ?? undefined, projectId, isV2: true },
    { enabled: shouldFetch },
  );

  const hasAnyData = Boolean(hasData.data);

  const find = analyticsRouter.find.useQuery(
    {
      query: {
        ...query,
        targetCurrency: currency,
      },
      workspaceId: workspaceId ?? undefined,
      projectId,
      isV2: true,
    },
    { enabled: shouldFetch && hasAnyData },
  );

  const mainRangeId = query.ranges.mainRange.id;

  const rangeResults = find.data?.rangeResults;

  const mainRangeResults = React.useMemo(() => {
    return (
      rangeResults?.filter(
        (rangeResultObj) => rangeResultObj.rangeId === mainRangeId,
      ) ?? []
    );
  }, [rangeResults, mainRangeId]);

  if (shouldBlurFullScreen) {
    const dummyMainRangeResults = Array.from({ length: 10 }, () => {
      return generateFakeChartDataRangeResult();
    });

    return {
      isLoading: false,
      mainRangeResults: dummyMainRangeResults,
    };
  }

  return {
    isLoading: find.isLoading || hasData.isLoading || isUrlHostsLoading,
    mainRangeResults,
  };
};

export default useDirectAnalyticsRead;
